import React from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import { visuallyHidden } from "@mui/utils";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { numberFormatter } from "utils/HelperFunctions";

function descendingComparator(a, b, orderBy) {
  let valueA = a[orderBy];
  let valueB = b[orderBy];
  if (!valueA) return 1;
  if (!valueB) return -1;

  if (["percentage", "totalAchievedPoints", "totalKpiCompleted"].includes(orderBy)) {
    valueA = parseFloat(valueA);
    valueB = parseFloat(valueB);
  } else {
    valueA = valueA?.toLowerCase();
    valueB = valueB?.toLowerCase();
  }
  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SortableLabel = React.memo(
  ({ labelKey, children, order, orderBy, handleRequestSort }) => (
    <TableSortLabel
      active={orderBy === labelKey}
      direction={orderBy === labelKey ? order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
    >
      {children}
      {orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.order === nextProps.order &&
    prevProps.orderBy === nextProps.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

const TableListingForManagerListView = React.memo(
  ({ data, setSelectedTeam, order, setOrder, orderBy, setOrderBy }) => {
    const handleRequestSort = (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const visibleRows = React.useMemo(() => {
      const teamData = [...data];
      return teamData.sort(getComparator(order, orderBy));
    }, [order, orderBy, data]);

    return (
      <MDBox>
        <Grid
          container
          spacing={2}
          sx={{
            "& .MuiPaper-root": {},
          }}
        >
          <Grid item xs={12} sx={{ position: "relative" }}>
            <TableContainer
              component={Paper}
              sx={{
                userSelect: "none",
                height: "100%",
              }}
              data-testid="team-list-table-container"
            >
              <Table aria-label="simple table">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell align="left">
                      <SortableLabel
                        labelKey="name"
                        order={order}
                        orderBy={orderBy}
                        handleRequestSort={handleRequestSort}
                      >
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          Team
                        </MDTypography>
                      </SortableLabel>
                    </TableCell>
                    <TableCell align="center">
                      <SortableLabel
                        labelKey="percentage"
                        order={order}
                        orderBy={orderBy}
                        handleRequestSort={handleRequestSort}
                      >
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          Daily Achievement
                        </MDTypography>
                      </SortableLabel>
                    </TableCell>
                    <TableCell align="center">
                      <SortableLabel
                        labelKey="totalAchievedPoints"
                        order={order}
                        orderBy={orderBy}
                        handleRequestSort={handleRequestSort}
                      >
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          Status
                        </MDTypography>
                      </SortableLabel>
                    </TableCell>
                    <TableCell align="center">
                      <SortableLabel
                        labelKey="totalKpiCompleted"
                        order={order}
                        orderBy={orderBy}
                        handleRequestSort={handleRequestSort}
                      >
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          Total KPIs
                        </MDTypography>
                      </SortableLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows?.map((row) => (
                    <TableRow
                      sx={{
                        cursor: "pointer",
                      }}
                      key={row.teamId}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedTeam(row)}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="end"
                          data-testid={`percentage-display-${row?.teamId}`}
                        >
                          <Typography sx={{ color: row.hexColor, fontSize: "1.5rem" }}>
                            {Math.round(numberFormatter(row.percentage || 0), 10)}
                          </Typography>
                          <Typography
                            sx={{
                              color: row.hexColor,
                              fontSize: "0.8rem",
                              pl: "0.2rem",
                              pb: "0.3rem",
                            }}
                          >
                            %
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" width="100%" height="100%">
                          <Stack direction="row" justifyContent="space-between" alignItems="end">
                            <Stack
                              direction="row"
                              justifyContent="center"
                              alignItems="end"
                              data-testid={`points-display-${row?.teamId}`}
                            >
                              <Typography sx={{ fontStyle: "italic", fontSize: "1.5rem" }}>
                                {row.totalAchievedPoints}
                              </Typography>
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  fontSize: "0.8rem",
                                  pl: "0.2rem",
                                  pb: "0.3rem",
                                }}
                              >
                                points
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              justifyContent="center"
                              alignItems="end"
                              data-testid={`target-display-${row?.teamId}`}
                            >
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  fontSize: "1.5rem",
                                  ...(row.totalAchievedPoints >= row.totalCompletedKpis
                                    ? {}
                                    : { color: "#888" }),
                                }}
                              >
                                {row.totalCompletedKpis}
                              </Typography>
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  fontSize: "0.8rem",
                                  pl: "0.2rem",
                                  pb: "0.3rem",
                                  ...(row.totalAchievedPoints >= row.totalCompletedKpis
                                    ? {}
                                    : { color: "#888" }),
                                }}
                              >
                                target
                              </Typography>
                            </Stack>
                          </Stack>
                          <Box height="25px" width="99%">
                            <Bar options={row.barOptions} data={row.barData} />
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <MDTypography sx={{ fontSize: "1.5rem" }}>
                          {row.totalKpiCompleted || 0}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  }
);

export default TableListingForManagerListView;
